import type { TIconName } from 'icons';
import type { TPermission } from '@/constants/permissions';

type TPersona =
  | 'recruiter'
  | 'recruiter_manager'
  | 'concierge'
  | 'concierge_manager'
  | 'super_admin';

type TNavBadgeKey =
  | 'messages'
  | 'new-applicants'
  | 'my-applicants'
  | 'failed-background-check'
  | 'verified'
  | 'my-clients'
  | 'my-singles';

type TNavBadges = Partial<Record<TNavBadgeKey, number>>;

type TNavRow = {
  label: string;
  href?: string;
  icon?: TIconName;
  count?: number;
  badgeKey?: TNavBadgeKey;
  permission?: TPermission;
};

type TNavSection = {
  eyebrow?: string;
  rows: TNavRow[];
};

const PERSONAS = [
  'recruiter',
  'recruiter_manager',
  'concierge',
  'concierge_manager',
  'super_admin',
] as const satisfies readonly TPersona[];

const PERSONA_LABELS: Record<TPersona, string> = {
  'recruiter': 'Recruiter',
  'recruiter_manager': 'Recruiter Manager',
  'concierge': 'Concierge',
  'concierge_manager': 'Concierge Manager',
  'super_admin': 'Super Admin',
};

const HOME_SECTION: TNavSection = {
  rows: [{
    label: 'Home',
    href: '/',
    permission: 'home:view',
  }],
};

const PIPELINE_SECTION: TNavSection = {
  eyebrow: 'Pipeline',
  rows: [
    { label: 'Pipeline overview', href: '/pipeline-overview', permission: 'pipeline:overview' },
    {
      label: 'New Applicants Pool',
      href: '/new-applicants',
      permission: 'applicants:view-new',
      badgeKey: 'new-applicants',
    },
    {
      label: 'My Applicants',
      href: '/my-applicants',
      permission: 'applicants:view-own',
      badgeKey: 'my-applicants',
    },
    { label: 'All Applicants', href: '/all-applicants', permission: 'applicants:view-all' },
    { label: 'Applicants (Boss)', href: '/boss-applicants', permission: 'boss-applicants:manage' },
    { label: 'My Interviews (list)', href: '/my-interviews', permission: 'interviews:list-own' },
    { label: 'All Interviews (list)', href: '/all-interviews', permission: 'interviews:list-all' },
    {
      label: 'My Interviews (calendar)',
      href: '/my-interviews-calendar',
      permission: 'interviews:calendar-own',
    },
    {
      label: 'Interviews (calendar)',
      href: '/interviews-calendar',
      permission: 'interviews:calendar-all',
    },
    {
      label: 'Background Check',
      href: '/background-check',
      permission: 'background-check:view',
    },
    {
      label: 'Failed Background Check',
      href: '/failed-background-check',
      permission: 'background-check:view-failed',
      badgeKey: 'failed-background-check',
    },
    {
      label: 'Further Review',
      href: '/further-review',
      permission: 'further-review:view',
    },
    { label: 'Final Approval', href: '/final-approval', permission: 'final-approval:decide' },
    {
      label: 'Verified',
      href: '/verified',
      permission: 'verified:view',
      badgeKey: 'verified',
    },
    { label: 'Approved', href: '/approved', permission: 'approved:view' },
    {
      label: 'Waitlist',
      href: '/waitlist',
      permission: 'waitlist:manage',
    },
    {
      label: 'Capacity',
      href: '/capacity',
      permission: 'capacity:view',
    },
    { label: 'Activation ready', href: '/activation-ready', permission: 'activation:view' },
    { label: 'On the App', href: '/on-the-app', permission: 'on-the-app:view' },
    { label: 'Invitations', href: '/invitations', permission: 'invitations:manage' },
  ],
};

const CLIENTS_SECTION: TNavSection = {
  eyebrow: 'Clients',
  rows: [
    {
      label: 'My Clients',
      href: '/my-clients',
      permission: 'clients:view-own',
      badgeKey: 'my-clients',
    },
    { label: 'All Clients', href: '/all-clients', permission: 'clients:view-all' },
  ],
};

const SINGLES_SECTION: TNavSection = {
  eyebrow: 'Singles',
  rows: [
    {
      label: 'My Singles',
      href: '/my-singles',
      permission: 'singles:view-own',
      badgeKey: 'my-singles',
    },
    { label: 'All Singles', href: '/all-singles', permission: 'singles:view-all' },
    {
      label: 'Singles search',
      href: '/singles-search',
      permission: 'singles:search',
    },
  ],
};

const RECS_SECTION: TNavSection = {
  eyebrow: 'Recs',
  rows: [
    { label: 'Create Rec', href: '/create-rec', permission: 'recs:create' },
    { label: 'My Recs', href: '/recs', permission: 'recs:view-own' },
  ],
};

const MESSAGES_SECTION: TNavSection = {
  eyebrow: 'Messages',
  rows: [{
    label: 'Messages',
    href: '/messages',
    permission: 'messages:view',
    badgeKey: 'messages',
  }],
};

const MEMBERS_SECTION: TNavSection = {
  eyebrow: 'Members',
  rows: [
    {
      label: 'Clients',
      href: '/members/clients',
      permission: 'members:manage',
    },
    {
      label: 'Singles',
      href: '/members/singles',
      permission: 'members:manage',
    },
  ],
};

const BILLING_SECTION: TNavSection = {
  eyebrow: 'Billing',
  rows: [
    {
      label: 'Billing',
      href: '/billing',
      permission: 'billing:desk',
    },
    { label: 'Need Review', href: '/billing/need-review', permission: 'billing:review' },
    {
      label: 'Overall & History',
      href: '/billing/overall',
      permission: 'billing:view-all',
    },
  ],
};

const ADMIN_SECTION: TNavSection = {
  eyebrow: 'Admin',
  rows: [
    {
      label: 'Concierges',
      href: '/concierges',
      permission: 'admin:concierges',
    },
    {
      label: 'Admin Manage',
      href: '/admin-manage',
      permission: 'admin:manage',
    },
    { label: 'Notifications & Settings', href: '/notifications', permission: 'admin:notifications' },
    {
      label: 'Invitation policy',
      href: '/invitation-policy',
      permission: 'admin:invitation-policy',
    },
    { label: 'Safety reports', href: '/safety-reports', permission: 'admin:safety-reports' },
    { label: 'Audit log', href: '/audit-log', permission: 'admin:audit-log' },
  ],
};

const NAVIGATION: TNavSection[] = [
  HOME_SECTION,
  PIPELINE_SECTION,
  CLIENTS_SECTION,
  SINGLES_SECTION,
  RECS_SECTION,
  MESSAGES_SECTION,
  MEMBERS_SECTION,
  BILLING_SECTION,
  ADMIN_SECTION,
];

const GUARDED_ROUTES: TNavRow[] = [
  { label: 'Member record', href: '/members', permission: 'members:view' },
];

const FOOTER_ROWS: TNavRow[] = [
  { label: 'Settings', href: '/settings', icon: 'settings' },
];

const SIGN_OUT_ROW: TNavRow = { label: 'Log out', icon: 'log-out' };

export type {
  TNavBadgeKey,
  TNavBadges,
  TPersona,
  TNavRow,
  TNavSection,
};
export {
  PERSONAS,
  PERSONA_LABELS,
  NAVIGATION,
  FOOTER_ROWS,
  GUARDED_ROUTES,
  SIGN_OUT_ROW,
};
